import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider } from "react-hook-form";

import Box from "@mui/material/Box";

import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";

import FormPhoneField from "@covertree/ui/forms/FormPhoneField";

import { DEFAULT_NO_EMAIL_BASE } from "@covertree/consts";
import { useFormWithNotification } from "@covertree/hooks";
import { BasicPolicyQuestion } from "@covertree/new-business-graphql";
import {
  useNewBusinessSelector,
  selectIsBookConversion,
} from "@covertree/new-business-redux";
import { theme } from "@covertree/theme";
import {
  Button,
  Form,
  FormEmailAutocorrect,
  FormToggleCard,
  Modal,
} from "@covertree/ui";
import { Toast } from "@covertree/utils";

import { yupResolver } from "@hookform/resolvers/yup";

import { ContactDetailsForm, contactDetailsFormValidator } from "./form";

interface Props {
  isD2C: boolean;
  emailAddress: string;
  noEmail?: boolean;
  noEmailQuestion?: BasicPolicyQuestion;
  onClose(): void;
  onSubmit(data: ContactDetailsForm): Promise<void>;
  open: boolean;
  physicallyMailDocuments?: boolean;
  physicallyMailDocumentsQuestion?: BasicPolicyQuestion;
  primaryContactNumber: string;
  secondaryContactNumber?: string;
}

export function UpdateContactDetailsModal({
  isD2C,
  emailAddress,
  noEmail,
  noEmailQuestion,
  onClose,
  onSubmit,
  open,
  physicallyMailDocuments,
  physicallyMailDocumentsQuestion,
  primaryContactNumber,
  secondaryContactNumber,
}: Props) {
  const [isLoading, setIsLoading] = useState(false);

  const isBookConversion = useNewBusinessSelector(selectIsBookConversion);

  const defaultValues: ContactDetailsForm = useMemo(
    () => ({
      emailAddress: noEmail ? "" : emailAddress,
      noEmail,
      physicallyMailDocuments,
      primaryContactNumber,
      secondaryContactNumber,
    }),
    [
      emailAddress,
      noEmail,
      physicallyMailDocuments,
      primaryContactNumber,
      secondaryContactNumber,
    ]
  );

  const form = useFormWithNotification({
    defaultValues,
    mode: "all",
    reValidateMode: "onSubmit",
    resolver: yupResolver(contactDetailsFormValidator(isBookConversion)),
  });

  const {
    control,
    formState: { isDirty },
    handleSubmit: submit,
    reset,
    setValue,
    watch,
  } = form;

  const handleSubmit = useCallback(
    async ({
      emailAddress,
      noEmail,
      physicallyMailDocuments,
      primaryContactNumber,
      secondaryContactNumber,
    }: ContactDetailsForm) => {
      try {
        if (!isDirty) {
          onClose();
          return;
        }
        setIsLoading(true);
        await onSubmit({
          emailAddress: noEmail
            ? `${DEFAULT_NO_EMAIL_BASE}${primaryContactNumber}@covertree.com`
            : emailAddress,
          primaryContactNumber,
          secondaryContactNumber,
          ...(noEmailQuestion ? { noEmail } : {}),
          ...(physicallyMailDocumentsQuestion
            ? { physicallyMailDocuments }
            : {}),
        });
        onClose();
      } catch {
        Toast.showError("Could not update contact information");
      } finally {
        setIsLoading(false);
      }
    },
    [
      isDirty,
      noEmailQuestion,
      onClose,
      onSubmit,
      physicallyMailDocumentsQuestion,
    ]
  );

  const emailAddressValue = watch("emailAddress");
  const noEmailValue = watch("noEmail");
  const physicallyMailDocumentsValue = watch("physicallyMailDocuments");

  useEffect(() => {
    if (!open) {
      reset(defaultValues);
    }
  }, [defaultValues, open, reset]);

  useEffect(() => {
    if (noEmailValue) {
      setValue("emailAddress", "");
    }
  }, [setValue, noEmailValue, emailAddress, emailAddressValue]);

  useEffect(() => {
    if (!!noEmailValue || emailAddressValue === "") {
      setValue("physicallyMailDocuments", !!noEmailValue);
    }
  }, [noEmailValue]);

  return (
    <Modal
      open={open}
      title="Edit contact information"
      onClose={onClose}
      disableClose={isLoading}
      maxWidth="md">
      <Form onSubmit={submit(handleSubmit)}>
        <FormProvider {...form}>
          <Grid container spacing={2}>
            <Grid item xs={isD2C ? 12 : 6}>
              <FormPhoneField
                id="phone-number-input"
                dataTestId="phone-number-input"
                name="primaryContactNumber"
                label="Phone number"
                control={control}
                disabled={isLoading}
              />
            </Grid>
            {!isD2C && (
              <Grid item xs={6}>
                <FormPhoneField
                  id="second-phone-number-input"
                  dataTestId="second-phone-number-input"
                  name="secondaryContactNumber"
                  label="Secondary phone number"
                  control={control}
                  disabled={isLoading}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormEmailAutocorrect
                id="email-input"
                dataTestId="email-input"
                name="emailAddress"
                label="Email"
                control={control}
                setValue={setValue}
                disabled={noEmailValue || isLoading}
                isEnabled={!noEmailValue}
              />
            </Grid>
            {noEmailQuestion && (
              <Grid item xs={12}>
                <FormToggleCard
                  disabled={isLoading}
                  title={noEmailQuestion.label}
                  checked={!!noEmailValue}
                  checkedCustomStyles={{
                    backgroundColor: theme.palette.secondary.contrastText,
                  }}
                  tooltip={noEmailQuestion.tooltip ?? undefined}
                  onToggle={() =>
                    setValue("noEmail", !noEmailValue, {
                      shouldDirty: true,
                    })
                  }
                />
              </Grid>
            )}
            {physicallyMailDocumentsQuestion && (
              <Grid item xs={12}>
                <FormToggleCard
                  disabled={noEmailValue || isLoading}
                  title={physicallyMailDocumentsQuestion.label}
                  checked={!!physicallyMailDocumentsValue}
                  checkedCustomStyles={{
                    backgroundColor: theme.palette.secondary.contrastText,
                  }}
                  tooltip={physicallyMailDocumentsQuestion.tooltip ?? undefined}
                  onToggle={() =>
                    setValue(
                      "physicallyMailDocuments",
                      !physicallyMailDocumentsValue,
                      {
                        shouldDirty: true,
                      }
                    )
                  }
                />
              </Grid>
            )}
          </Grid>
        </FormProvider>
        <Box mt={5} mb={2}>
          <DialogActions>
            <Button
              type="submit"
              data-testid="submit-edit-contact-form"
              loading={isLoading}
              disabled={isLoading}>
              Submit
            </Button>
            <Button
              disabled={isLoading}
              variant="outlined"
              onClick={onClose}
              data-testid="cancel-edit-contact-form">
              Cancel
            </Button>
          </DialogActions>
        </Box>
      </Form>
    </Modal>
  );
}
